export const MCP_OAUTH_MESSAGE_SOURCE = 'shahagent:mcp-oauth';

const POPUP_NAME = 'shahagent-mcp-oauth';
const POPUP_WIDTH = 620;
const POPUP_HEIGHT = 780;

export type McpOAuthResult = {
  status: string;
  serverId: string;
};

type McpOAuthPopupMessage = McpOAuthResult & {
  source: typeof MCP_OAUTH_MESSAGE_SOURCE;
};

/**
 * Must be called synchronously from the click handler, otherwise the browser treats the
 * later `window.open` as unsolicited and blocks it. The window starts blank and is pointed
 * at the vendor once the authorize URL comes back.
 */
export const openMcpOAuthPopup = (): Window | null => {
  const left = Math.round(window.screenX + Math.max(0, (window.outerWidth - POPUP_WIDTH) / 2));
  const top = Math.round(window.screenY + Math.max(0, (window.outerHeight - POPUP_HEIGHT) / 2));

  return window.open(
    'about:blank',
    POPUP_NAME,
    `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`,
  );
};

/** Returns false when the popup is gone, so the caller can fall back to a full-page redirect. */
export const sendPopupTo = (popup: Window | null, url: string): boolean => {
  if (!popup || popup.closed) {
    return false;
  }

  try {
    popup.location.replace(url);
    popup.focus();
    return true;
  } catch {
    return false;
  }
};

export const closeMcpOAuthPopup = (popup: Window | null): void => {
  if (popup && !popup.closed) {
    popup.close();
  }
};

export const readMcpOAuthMessage = (event: MessageEvent): McpOAuthResult | null => {
  if (event.origin !== window.location.origin) {
    return null;
  }

  const data = event.data as Partial<McpOAuthPopupMessage> | null;
  if (!data || data.source !== MCP_OAUTH_MESSAGE_SOURCE || typeof data.status !== 'string') {
    return null;
  }

  return {
    status: data.status,
    serverId: typeof data.serverId === 'string' ? data.serverId : '',
  };
};

/**
 * Runs before the app renders. When the OAuth callback lands inside the popup, the result is
 * handed to the tab that started the flow and the popup closes, so that tab keeps its state.
 * Returns true when this window is a popup that has been dealt with and should not render.
 */
export const completeMcpOAuthPopup = (): boolean => {
  let opener: Window | null = null;
  try {
    opener = window.opener as Window | null;
  } catch {
    return false;
  }

  if (!opener || opener === window || opener.closed) {
    return false;
  }

  const status = new URLSearchParams(window.location.search).get('mcpOAuth');
  if (!status) {
    return false;
  }

  const message: McpOAuthPopupMessage = {
    source: MCP_OAUTH_MESSAGE_SOURCE,
    status,
    serverId: new URLSearchParams(window.location.search).get('serverId') ?? '',
  };

  try {
    opener.postMessage(message, window.location.origin);
  } catch {
    return false;
  }

  window.close();
  return true;
};
